// Ch78 · THE LEADS WORK LIST — pure model, no React.
//
// Grant's IA ruling (2026-09-08): "Messages = REAL clients. The Leads page is
// everyone who ISN'T a client yet — form landing through first real
// conversation. Someone converted but never contacted is STILL a lead here."
//
// So this module answers two questions and nothing else:
//
//   WHO IS ON THIS PAGE?  Every lead that is not archived, plus every client
//   still sitting at `new_client` — converted, but never advanced. A client
//   who is touring / applied / paid / closed has had the real conversation and
//   belongs in Messages; putting them here would make the page a second, worse
//   client list. `out_of_contact` is a lapsed client, not a lead.
//
//   WHAT DOES IT COST TO KEEP WAITING?  A work list, not a spreadsheet. Each
//   person carries the stage they are stuck in, how long they have been stuck,
//   and ONE next action. The order is the cost of waiting (see `waitingCost`),
//   which is a stated heuristic over stated fields — never a claim of insight.
//
// Everything here is derived from rows the page already loads: `leads`,
// `clients`, and the `lead_claims` ledger migration 049 writes. A field we
// cannot read is null and renders as "—"; it is never guessed into a number.

import type { BoardPerson } from "./moveInBoard";
import { deriveLeadSourceLabel } from "../components/clients/leadQuality";
import { isEngaged, type QueueClient } from "../components/messages/clientQueue";
import { DROPPED_STATUSES } from "../components/dashboard/yourClientsScore";

/** The claim ledger, as the dashboard already reads it (migration 049). */
export interface LeadClaimRow {
  lead_id: string;
  owner_user_id?: string | null;
  claimed_at?: string | null;
  converted_client_id?: string | null;
}

export type LeadStageKey =
  | "returned"
  | "unclaimed"
  | "claimed_uncontacted"
  | "converted_uncontacted"
  | "contacted_waiting"
  | "expired"
  | "archived";

/** Top to bottom in the work list: what it costs to leave the group alone. */
export const STAGE_ORDER: readonly LeadStageKey[] = [
  // First, because it is the most valuable row on the page and the easiest to
  // lose: somebody who already knows us filled the form in AGAIN and nobody has
  // answered since. The old feed made these vanish entirely (they matched an
  // existing client), which is the defect lead_flow_truth.returning_renters
  // exists to name.
  "returned",
  "unclaimed",
  "claimed_uncontacted",
  "converted_uncontacted",
  "contacted_waiting",
  "expired",
  "archived",
];

export const STAGE_LABEL: Record<LeadStageKey, string> = {
  returned: "Came back — no answer since",
  unclaimed: "Unclaimed",
  claimed_uncontacted: "Claimed, not yet contacted",
  converted_uncontacted: "Converted, no contact yet",
  contacted_waiting: "Contacted, waiting on them",
  expired: "Expired",
  archived: "Archived",
};

/**
 * Who is a CLIENT rather than a lead — read from the one definition Messages
 * already uses (`clientQueue.isEngaged`), not from a second one written here.
 *
 * `isEngaged` says a client is engaged when there is evidence of a
 * RELATIONSHIP and not merely a row: they texted us · they said we may text
 * them · they owe money · they moved past `new_client`. That is Grant's ruling
 * exactly, and it is strictly richer than the status check this page shipped
 * with: a converted lead who REPLIED is engaged even while their status still
 * says `new_client`, and belongs in Messages.
 *
 * Two things it does not decide, and this function does:
 *
 *   DROPPED clients (`out_of_contact` · `closed`) are not engaged, but they are
 *   not leads either — they are finished. `isEngaged` alone would hand them
 *   back to the top of the funnel.
 *
 *   Its three richest clauses read fields that live on `GET /clients/queue`
 *   (`last_inbound_at`, `consent_state`, `open_invoice`) and NOT on the
 *   `clients` table. Handed a raw table row it silently degrades to the status
 *   check while looking like the full definition — the exact shape of a guard
 *   that covers nothing. So the signals are passed in explicitly, and when
 *   they are missing the caller is told the answer is degraded rather than
 *   being allowed to believe otherwise.
 */
export function isLeadsPageClient(
  client: { status?: string | null; id?: string } | null | undefined,
  signals: QueueClient | undefined,
): boolean {
  const status = (client?.status || "").toLowerCase();
  if (DROPPED_STATUSES.has(status)) return false; // finished, not a lead
  if (signals) return !isEngaged(signals);
  // Degraded: the only engagement signal a raw clients row carries is stage.
  return status === "new_client";
}

const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;
/** Unclaimed this long is the first breach that shows on the page. */
export const OVERDUE_HOURS = 24;
/**
 * Waiting SATURATES here. Cost used to be linear in hours waited, which meant
 * age drowned every other factor: a lead that arrived 201 days ago scored 200x
 * a lead that arrived yesterday, so no move-in date, tier or budget could ever
 * outrank it, and production duly put a 201-day-old row at the top of the list
 * on day one. Waiting two weeks is a failure; waiting two hundred days is not
 * fourteen times worse than waiting fourteen — it is the same failure, older.
 */
export const WAITING_SATURATION_HOURS = 14 * 24;
/** Unclaimed AND the move-in day passed this long ago: expired, never deleted. */
export const EXPIRY_DAYS = 30;

function parseMs(raw: string | null | undefined): number | null {
  if (!raw) return null;
  const t = Date.parse(raw);
  return Number.isFinite(t) ? t : null;
}

/** "3h" · "31h" · "3d" · "—" when we cannot say. Hours below two days. */
export function waitingLabel(ms: number | null): string {
  if (ms === null || ms < 0) return "—";
  const hours = Math.floor(ms / HOUR_MS);
  if (hours < 48) return `${hours}h`;
  return `${Math.floor(ms / DAY_MS)}d`;
}

/**
 * The cost of waiting — the work list's sort key, biggest first.
 *
 * A stated heuristic over stated fields, not a prediction: how long they have
 * waited, multiplied by how soon they must move and by how credible the scorer
 * thinks they are, with budget as the tiebreaker. It is explained verbatim in
 * the list's (i) hover so nobody mistakes it for a model.
 */
export function waitingCost(person: BoardPerson, waitingMs: number | null, todayMs: number): number {
  const hours = Math.min(
    waitingMs === null ? 0 : waitingMs / HOUR_MS,
    WAITING_SATURATION_HOURS,
  );
  let urgency = 1;
  if (person.dateMs !== null) {
    const days = (person.dateMs - todayMs) / DAY_MS;
    if (days < 0) {
      // A passed move-in DECAYS. Yesterday's is urgent — they may still be
      // looking. One that passed six months ago is not more urgent than a
      // person moving in three weeks, and a flat multiplier said it was:
      // production put a 201-day-old past-due lead at the top of the list
      // above everybody with a live date. Full weight the day it passes,
      // ordinary weight a month later, a floor after ten weeks.
      // Full weight the day it passes, ordinary weight a month later, and a
      // floor of 0.1 by the time a quarter has gone by — somebody whose
      // move-in passed three months ago has housed themselves. Never zero:
      // they stay on the page, they just stop shouting over live work.
      urgency = Math.min(1.5, Math.max(0.1, 1.5 + days / 60));
    } else if (days <= 30) urgency = 2;
    else if (days <= 90) urgency = 1.25;
  }
  const credibility =
    person.tone === "fake" ? 0.25 : person.tone === "real" ? 1.25 : 1;
  return hours * urgency * credibility + (person.budget ?? 0) / 1000;
}

/**
 * The one thing to do next. One verb, and the reason when there is one.
 *
 * `askNext` is the living profile's own question for this person (the field it
 * most wants answered). When we have it, it beats a generic verb: "Send text ·
 * ask their move-in" tells the operator what to type.
 */
export function nextAction(
  stage: LeadStageKey,
  person: BoardPerson,
  askNext?: string | null,
): string {
  if (stage === "returned") {
    return person.textsOk ? "Send text · they came back" : "Call · they came back";
  }
  if (stage === "archived") return "Restore";
  if (stage === "expired") return "Archive";
  if (stage === "unclaimed") {
    if (person.tone === "fake") return "Archive";
    if (person.budget === null) return "Claim · then ask budget";
    if (person.dateMs === null) return "Claim · then ask move-in";
    return "Claim";
  }
  if (stage === "claimed_uncontacted") {
    if (person.tone === "unscored") return "Verify";
    if (askNext) return `Text · ${askNext}`;
    if (person.dateMs === null) return "Text · confirm move-in";
    return "Text";
  }
  if (stage === "converted_uncontacted") {
    const verb = person.textsOk ? "Send text" : "Call · no text consent";
    return askNext && person.textsOk ? `Send text · ${askNext}` : verb;
  }
  return askNext ? `Follow up · ${askNext}` : "Follow up";
}

export interface WorkRow {
  /** `lead:<id>` / `client:<id>` — the BoardPerson key, stable across renders. */
  key: string;
  person: BoardPerson;
  stage: LeadStageKey;
  /** How long they have been waiting on US at this stage. null = unreadable. */
  waitingMs: number | null;
  waitingLabel: string;
  /** Unclaimed past OVERDUE_HOURS — the red number in the group header. */
  overdue: boolean;
  /** Where they came from, or null. Never invented for a client with no lead. */
  sourceLabel: string | null;
  ownerId: string | null;
  cost: number;
  nextAction: string;
}

function stageOf(
  person: BoardPerson,
  claimed: Map<string, LeadClaimRow>,
  todayMs: number,
  signals: Map<string, QueueClient> | null,
): LeadStageKey | null {
  if (person.kind === "client") {
    const client = person.client;
    // Checked BEFORE the engagement gate on purpose: a returning renter is
    // usually an engaged client, and gating first is exactly how the old feed
    // made them disappear. A new inquiry nobody has answered is work.
    const returned = Date.parse(person.returnedAt || "");
    const contacted = Date.parse(client?.last_contacted_at || "");
    if (Number.isFinite(returned) && !(Number.isFinite(contacted) && contacted >= returned)) {
      return "returned";
    }
    if (!isLeadsPageClient(client, signals?.get(client?.id ?? ""))) return null; // Messages owns them
    return client?.last_contacted_at ? "contacted_waiting" : "converted_uncontacted";
  }
  const lead = person.lead;
  if (!lead) return null;
  const status = (lead.status || "").toLowerCase();
  // Ch78 · archived leads are HIDDEN, not gone. This used to return null, which
  // put them outside the page entirely — and since the work list can archive in
  // bulk, that made Archive a one-way door: a lead you archived by mistake
  // vanished with no way to see it, confirm it, or undo it. The row was still
  // in the table; the UI simply had no route back to it.
  //
  // The rule the rest of this page already follows: expired leads are folded
  // shut by default, never deleted, one click away. Archive works the same now.
  if (status === "archived") return "archived";
  // A converted lead with no client row we can see is still converted; we just
  // cannot read a contact time for them, so they sit in the honest bucket.
  if (status === "converted") return "converted_uncontacted";
  if (claimed.has(lead.id)) return "claimed_uncontacted";
  if (person.dateMs !== null && person.dateMs < todayMs - EXPIRY_DAYS * DAY_MS) return "expired";
  return "unclaimed";
}

function waitingFor(
  person: BoardPerson,
  stage: LeadStageKey,
  claim: LeadClaimRow | undefined,
  nowMs: number,
): number | null {
  let since: number | null = null;
  if (stage === "returned") since = parseMs(person.returnedAt);
  else if (stage === "contacted_waiting") since = parseMs(person.client?.last_contacted_at);
  else if (stage === "converted_uncontacted") since = parseMs(person.client?.created_at) ?? parseMs(person.lead?.created_at);
  else if (stage === "claimed_uncontacted") since = parseMs(claim?.claimed_at) ?? parseMs(person.lead?.created_at);
  else since = parseMs(person.lead?.created_at);
  if (since === null) return null;
  return Math.max(0, nowMs - since);
}

/** Every person on this page as a work row, costliest wait first. */
export function buildWorkRows(
  people: BoardPerson[],
  claims: LeadClaimRow[] | null,
  now: Date,
  /**
   * Engagement signals by client id, from `GET /clients/queue`. `null` means
   * that read failed — the partition falls back to the status check and the
   * PAGE says so, rather than quietly showing engaged clients as leads.
   */
  signals: Map<string, QueueClient> | null = null,
  /** The living profile's own next question per client id, when it is loaded. */
  asks: Map<string, string | null> | null = null,
): WorkRow[] {
  const nowMs = now.getTime();
  const todayMs = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const claimed = new Map((claims ?? []).map((c) => [c.lead_id, c]));
  const rows: WorkRow[] = [];
  for (const person of people) {
    const stage = stageOf(person, claimed, todayMs, signals);
    if (!stage) continue;
    const claim = person.lead ? claimed.get(person.lead.id) : undefined;
    const ms = waitingFor(person, stage, claim, nowMs);
    rows.push({
      key: person.key,
      person,
      stage,
      waitingMs: ms,
      waitingLabel: waitingLabel(ms),
      overdue: stage === "unclaimed" && ms !== null && ms >= OVERDUE_HOURS * HOUR_MS,
      sourceLabel: person.lead
        ? deriveLeadSourceLabel(person.lead)
        : person.client?.lead_source?.trim() || null,
      ownerId: claim?.owner_user_id ?? person.agentId,
      cost: waitingCost(person, ms, todayMs),
      nextAction: nextAction(stage, person, asks?.get(person.client?.id ?? "") ?? null),
    });
  }
  rows.sort((a, b) => b.cost - a.cost || a.key.localeCompare(b.key));
  return rows;
}

export interface WorkGroup {
  key: LeadStageKey;
  label: string;
  rows: WorkRow[];
  /** The right-hand note in the header: what makes this group urgent. */
  note: string | null;
  /** Groups that are not the work: already-contacted, and expired. */
  collapsedByDefault: boolean;
}

function noteFor(key: LeadStageKey, rows: WorkRow[]): string | null {
  if (key === "returned") return "they already know us";
  if (key === "unclaimed") {
    const late = rows.filter((r) => r.overdue).length;
    return late ? `${late} over ${OVERDUE_HOURS}h` : null;
  }
  if (key === "claimed_uncontacted") {
    const oldest = rows.reduce<number | null>(
      (max, r) => (r.waitingMs === null ? max : max === null || r.waitingMs > max ? r.waitingMs : max),
      null,
    );
    return oldest === null ? null : `oldest ${waitingLabel(oldest)}`;
  }
  if (key === "converted_uncontacted") {
    const yes = rows.filter((r) => r.person.textsOk).length;
    return yes ? `they said yes to texts: ${yes}` : null;
  }
  return null;
}

/** The rows grouped under their stage headers, in STAGE_ORDER. Empty groups drop out. */
export function buildWorkGroups(rows: WorkRow[]): WorkGroup[] {
  return STAGE_ORDER.map((key) => {
    const inStage = rows.filter((r) => r.stage === key);
    return {
      key,
      label: STAGE_LABEL[key],
      rows: inStage,
      note: noteFor(key, inStage),
      collapsedByDefault:
        key === "contacted_waiting" || key === "expired" || key === "archived",
    };
  }).filter((g) => g.rows.length > 0);
}
